/**
 * Statutory screening floor for direct-care roster work.
 *
 * Aged Care Act 2024 / Rules 2025 require a lawful screening basis before a
 * worker delivers aged-care services. That obligation attaches to the worker
 * and the care — not to whether the slot named a requirement, and not to the
 * tenant's `worker_compliance_enforcement_mode`.
 *
 * Detection is independent of tenant enforcement: missing and expired evidence
 * is always evaluated and recorded. Enforcement is applied later by the shared
 * gate policy and database assignment wrappers. Under WARN, both screening
 * states remain informational backfill evidence; under ENFORCE_ROSTER or
 * ENFORCE_ALL they block, with MISSING_SCREENING retaining the provisional
 * attestation exit (statutory declaration + supervision + lodged application +
 * ≤90-day runway).
 *
 * Acceptable floor evidence (any one current clearance):
 *   NATIONAL_POLICE_CHECK | NDIS_WORKER_SCREENING | AGED_CARE_WORKER_SCREENING
 *   (WORKER_SCREENING_CLEARANCE is treated as the latter two)
 *
 * SINGLE SOURCE OF TRUTH.
 * `DIRECT_CARE_STAFF_ROLES` and the service-token lists below are the TS
 * authority. Migration `20270936000060` must list the same role codes and
 * service tokens. The drift test in
 * `packages/utils/src/__tests__/statutory-screening-floor-sql-parity.test.ts`
 * fails when either side drifts.
 */

import { isClassificationCareRole } from './roster-classification-requirement';

export const STATUTORY_SCREENING_FLOOR_TYPES = [
  'NATIONAL_POLICE_CHECK',
  'NDIS_WORKER_SCREENING',
  'AGED_CARE_WORKER_SCREENING',
] as const;

export type StatutoryScreeningFloorType = (typeof STATUTORY_SCREENING_FLOOR_TYPES)[number];

const STATUTORY_FLOOR_SET = new Set<string>([
  ...STATUTORY_SCREENING_FLOOR_TYPES,
  'WORKER_SCREENING_CLEARANCE',
]);

/**
 * Staff / required-role codes that mean the person is doing direct care.
 *
 * WHY NOT AGENCY_CONTRACTOR.
 * That token is an employment arrangement, not a care role. Agency cleaners and
 * agency caterers are not delivering personal care; treating the arrangement as
 * care would block Medea's ~1,315 non-care shifts (catering, cleaning, laundry,
 * maintenance, admin, activities). Direct care is determined by the WORK
 * (service type, service code, or a care required/staff role code) — employment
 * arrangement is irrelevant in both directions: an agency worker on personal
 * care is in scope; an internal worker on laundry is not.
 *
 * Medea tracks agency via shift.is_agency (130 agency shifts) with zero staff
 * carrying role=AGENCY_CONTRACTOR today; the exclusion still matters for any
 * future tenant that stores the arrangement on staff.role.
 */
export const DIRECT_CARE_STAFF_ROLES = [
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'ASSISTANT_IN_NURSING',
  'PERSONAL_CARE_WORKER',
  'CARE_WORKER',
  'CLINICAL_LEAD',
] as const;

export type DirectCareStaffRole = (typeof DIRECT_CARE_STAFF_ROLES)[number];

const DIRECT_CARE_ROLE_SET = new Set<string>(DIRECT_CARE_STAFF_ROLES);

/**
 * Service-type tokens mirrored into SQL `roster_assignment_is_direct_care`.
 * Free-text board values still also pass through `isClassificationCareRole`
 * (e.g. `RN 07.00 - 15.00` → NURSING) so the gate is not weaker than the
 * display classifier for known care families.
 *
 * MEALTIME is deliberate: assisting a resident to eat is personal care, not
 * kitchen work. Without it, "Mealtime Assistance" blocked at publish (TS
 * classifier) while SQL returned an empty floor.
 */
export const DIRECT_CARE_SERVICE_TYPE_TOKENS = [
  'PERSONAL_CARE',
  'NURSING',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'PCA',
  'PCW',
  'AIN',
  'CARE_WORKER',
  'CLINICAL',
  'MEALTIME',
] as const;

/** Service-code tokens mirrored into the SQL service_code arm. */
export const DIRECT_CARE_SERVICE_CODE_TOKENS = [
  'PERSONAL_CARE',
  'NURSING',
  'PCA',
  'PCW',
  'AIN',
  'CARE',
] as const;

const SCREENING_KNOWN_BAD_STATUSES = new Set([
  'EXPIRED',
  'REVOKED',
  'FAILED',
  'REJECTED',
  'EXCLUDED',
  'REQUIRES_REVIEW',
]);

function normaliseCode(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

function normaliseStatus(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .toUpperCase();
}

function serviceMatchesTokens(raw: string | null | undefined, tokens: readonly string[]): boolean {
  const upper = normaliseCode(raw);
  if (!upper) return false;
  return tokens.some((token) => upper.includes(token));
}

/** True when this assignment is direct-care work for the statutory floor. */
export function isDirectCareAssignment(input: {
  readonly serviceType?: string | null;
  readonly serviceCode?: string | null;
  readonly requiredRole?: string | null;
  readonly staffRole?: string | null;
}): boolean {
  // Free-text service families (RN/PCA/EN lines, personal care) — same display
  // classifier the board already uses so "RN 07.00 - 15.00" counts as care.
  if (
    isClassificationCareRole({
      serviceType: input.serviceType,
      serviceCode: input.serviceCode,
    })
  ) {
    return true;
  }
  // Token parity with SQL roster_assignment_is_direct_care (service arms).
  if (serviceMatchesTokens(input.serviceType, DIRECT_CARE_SERVICE_TYPE_TOKENS)) return true;
  if (serviceMatchesTokens(input.serviceCode, DIRECT_CARE_SERVICE_CODE_TOKENS)) return true;

  const required = normaliseCode(input.requiredRole);
  if (required && DIRECT_CARE_ROLE_SET.has(required)) return true;
  const staff = normaliseCode(input.staffRole);
  if (staff && DIRECT_CARE_ROLE_SET.has(staff)) return true;
  return false;
}

export function isStatutoryScreeningFloorType(type: string | null | undefined): boolean {
  return STATUTORY_FLOOR_SET.has(normaliseCode(type));
}

export type StatutoryScreeningFloorResult =
  | { readonly status: 'SATISFIED' }
  | { readonly status: 'MISSING' }
  | { readonly status: 'EXPIRED_OR_INVALID'; typeLabel: string; awaitingVerification: boolean }
  | { readonly status: 'NOT_APPLICABLE' };

export type StatutoryFloorScreeningRecord = {
  readonly screeningType: string;
  readonly status: string;
  readonly expiresAt: string | null;
};

/**
 * Evaluate the statutory screening floor for a direct-care assignment.
 * Returns NOT_APPLICABLE when the work is not direct care.
 */
export function evaluateStatutoryScreeningFloor(input: {
  readonly isDirectCare: boolean;
  readonly shiftDate: string; // YYYY-MM-DD
  readonly screenings: readonly StatutoryFloorScreeningRecord[];
}): StatutoryScreeningFloorResult {
  if (!input.isDirectCare) return { status: 'NOT_APPLICABLE' };

  let sawFloorType = false;
  let sawValid = false;
  let invalidLabel: string | null = null;
  let awaitingVerification = false;

  for (const sc of input.screenings) {
    if (!isStatutoryScreeningFloorType(sc.screeningType)) continue;
    sawFloorType = true;
    const status = normaliseStatus(sc.status);
    const expired = sc.expiresAt !== null && sc.expiresAt < input.shiftDate;
    const knownBad = SCREENING_KNOWN_BAD_STATUSES.has(status) || expired;
    if (knownBad) {
      const unverified = status === 'REQUIRES_REVIEW' && !expired;
      // Do not let an unverified record mask an expired or invalid record.
      if (invalidLabel === null || (awaitingVerification && !unverified)) {
        invalidLabel = sc.screeningType;
        awaitingVerification = unverified;
      }
      continue;
    }
    if (
      status === 'CLEARED' ||
      status === 'CONDITIONAL' ||
      status === 'CURRENT' ||
      status === 'VERIFIED'
    ) {
      sawValid = true;
    }
  }

  if (sawValid) return { status: 'SATISFIED' };
  if (sawFloorType && invalidLabel) {
    return { status: 'EXPIRED_OR_INVALID', typeLabel: invalidLabel, awaitingVerification };
  }
  return { status: 'MISSING' };
}
